import { useDebounce } from '@uidotdev/usehooks';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useLatest } from 'react-use';
import { remark } from 'remark';
import { match } from 'ts-pattern';

import { CopyRichTextButton } from '@eluve/blocks';
import {
  Button,
  Checkbox,
  Icon,
  SimpleTooltip,
  Slider,
  Stack,
  cn,
  useToast,
} from '@eluve/components';
import { EluveEditor } from '@eluve/eluve-editor';
import {
  useAmendments,
  useSnippetEditorExtension,
} from '@eluve/frontend-appointment-hooks';
import {
  Block,
  CheckboxBlock,
  GroupBlock,
  RangeBlock,
  TextFieldBlock,
} from '@eluve/llm-outputs';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { openMailTo } from '@eluve/navigator-utils';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  JsonValue,
  areStringsEqualWithoutSpecialCharacters,
} from '@eluve/utils';

import { Amendment } from './Amendment';
import { getHeadingPropsForDepth } from './TemplateBuilder/getHeadingPropsForDepth';

export const DEBOUNCE_MS = 500;

export type DynamicArtifactSaveContextProps = {
  saveBlock: (blockPath: string[], value: JsonValue) => Promise<void>;
  onPendingChanges: () => void;
  insertChartAmendment: (keyPath: string) => Promise<void>;
};

export const defaultArtifactSaveContextProps: DynamicArtifactSaveContextProps =
  {
    saveBlock: async () => noop(),
    onPendingChanges: () => noop(),
    insertChartAmendment: async () => noop(),
  };

export const DynamicArtifactSaveContext =
  createContext<DynamicArtifactSaveContextProps>(
    defaultArtifactSaveContextProps,
  );

export const useSaveBlock = () => {
  const context = useContext(DynamicArtifactSaveContext);
  if (!context) {
    throw new Error(
      'useDynamicArtifactSave must be used within an DynamicArtifactSaveContextProvider',
    );
  }

  return context.saveBlock;
};

export const useInsertAmendment = () => {
  const context = useContext(DynamicArtifactSaveContext);
  if (!context) {
    throw new Error(
      'useDynamicArtifactSave must be used within an DynamicArtifactSaveContextProvider',
    );
  }

  return context.insertChartAmendment;
};

export const useDebounceSaveBlock = <
  T extends string | number | boolean | { label: string; isChecked: boolean }[],
>(
  block: Block,
  currentValue: T,
) => {
  const { key } = block;
  const { keyPath } = useArtifactContext();
  const { onPendingChanges } = useContext(DynamicArtifactSaveContext);
  const [value, setValue] = useState<T>(currentValue);
  const debouncedValue = useDebounce(value, DEBOUNCE_MS);
  const latestValue = useLatest(currentValue);
  const saveBlock = useSaveBlock();
  // Track serialized values of pending saves so we can identify echoes.
  // Uses JSON.stringify to handle array types (checkbox options) where
  // Apollo cache returns new references for identical content.
  const pendingSavesRef = useRef(new Set<string>());

  useEffect(() => {
    if (currentValue !== value) {
      onPendingChanges();
    }
  }, [currentValue, value, onPendingChanges]);

  useEffect(() => {
    if (debouncedValue !== latestValue.current) {
      pendingSavesRef.current.add(JSON.stringify(debouncedValue));
      saveBlock([...keyPath, key], debouncedValue);
    }
  }, [debouncedValue, key, keyPath, latestValue, saveBlock]);

  // Sync internal state when currentValue changes from parent.
  // Skip if the incoming value matches a pending save — it's our own
  // mutation echoing back through the Apollo cache, not an external update.
  useEffect(() => {
    const serialized = JSON.stringify(currentValue);
    if (pendingSavesRef.current.has(serialized)) {
      pendingSavesRef.current.delete(serialized);
      return;
    }
    setValue(currentValue);
  }, [currentValue]);

  return [value, setValue] as const;
};

export const ArtifactContext = createContext<
  | {
      isReadonly: boolean;
      groupDepth: number;
      keyPath: string[];
      email?: string | null;
      canViewAndEditAmendments?: boolean;
      showActions?: boolean;
      compact?: boolean;
    }
  | undefined
>(undefined);

const useArtifactContext = () => {
  const context = useContext(ArtifactContext);
  if (!context) {
    throw new Error(
      'useArtifactContext must be used within an ArtifactContextProvider',
    );
  }
  return context;
};

// Transform single newlines to double newlines for display in EluveEditor
export const preserveNewlinesForMarkdown = (text: string): string => {
  return text.replace(/\n+/g, (match) => {
    // Only convert isolated single newlines to double; leave 2+ newlines unchanged
    if (match.length === 1) return '\n\n';
    return match;
  });
};

// Normalize double newlines back to single newlines for storage
export const normalizeNewlinesFromMarkdown = (text: string): string => {
  return text.replace(/\n+/g, (match) => {
    // Only convert exactly 2 newlines to 1; leave 1 or 3+ unchanged
    if (match.length === 2) return '\n';
    return match;
  });
};

const TextFieldBlockRenderer: React.FC<{ textField: TextFieldBlock }> = ({
  textField,
}) => {
  const {
    groupDepth,
    isReadonly,
    email,
    canViewAndEditAmendments,
    showActions,
    compact,
  } = useArtifactContext();

  const userId = useUserIdFromSession();
  const toast = useToast();

  const { snippetExtension, snippetCount } = useSnippetEditorExtension();

  const { label, text, key, isEmailEnabled } = textField;
  const contentRef = useRef<HTMLDivElement>(null);
  const { appointmentId, tenantId } = useParams() as {
    appointmentId: string | undefined;
    tenantId: string | undefined;
  };

  const [forceUpdate, setForceUpdate] = useState(0);
  useEffect(() => {
    if (isReadonly) {
      setForceUpdate((prev) => prev + 1);
    }
  }, [text, isReadonly]);

  const [localValue, setTextValue] = useDebounceSaveBlock(
    textField,
    text ?? '',
  );

  const depth = groupDepth + 1;
  const { tag: HeadingTag, className: headingClass } =
    getHeadingPropsForDepth(depth);

  const analytics = useAnalytics('artifacts');

  const handleMailTo = async () => {
    const summary = String(
      await remark()
        .data('settings', {
          setext: true,
        })
        .process(text),
    );

    const subject = `Follow up from today's session (${dayjs().format('MMM DD')})`;

    analytics.capture('email_client_recap_send', {
      tenantId,
      userId,
      appointmentId,
    });

    await openMailTo({
      to: email,
      subject,
      body: summary,
      onClipboardFallback: () => {
        toast.info('Email body copied to clipboard — paste it into your email');
      },
    });
  };
  const insertChartAmendment = useInsertAmendment();

  const addChartAmendment = async () => {
    await insertChartAmendment(key);
  };

  // DynamicArtifacts are rendered in places outside of the appointment context
  // so we can't always fetch amendments
  const { amendments } = useAmendments(appointmentId ?? '');
  const draftAmendment = amendments.find((a) => !a.submittedAt);
  const draftChartAmendment = draftAmendment?.chart_amendments.find(
    (chart) => chart.keyPath === key,
  );

  const canAddAmendment =
    canViewAndEditAmendments && draftAmendment && !draftChartAmendment;

  const content = useMemo(() => {
    const safeText =
      typeof localValue === 'string'
        ? localValue
        : Array.isArray(localValue)
          ? (localValue as unknown[]).join('\n')
          : String(localValue ?? '');
    return preserveNewlinesForMarkdown(safeText);
  }, [localValue]);

  return (
    <Stack gap={2}>
      <Stack gap={compact ? 1 : 2} direction="horizontal">
        {label && (
          <HeadingTag className={cn(headingClass, 'text-nowrap')}>
            {label}
          </HeadingTag>
        )}
        {showActions !== false && (
          <Stack justify="between" gap={compact ? 1 : 2} direction="horizontal">
            <Stack gap={compact ? 1 : 2} direction="horizontal">
              <CopyRichTextButton
                size={compact ? 'sm' : undefined}
                getContentElement={() => contentRef.current}
                markdownContent={text}
                onCopy={() => {
                  analytics.capture('appointment.note_section_copied', {
                    label,
                    appointmentId,
                    tenantId,
                  });
                }}
              />
              {isEmailEnabled && (
                <SimpleTooltip label="Send mail to patient">
                  <Button
                    variant="ghost"
                    size={compact ? 'sm' : undefined}
                    onClick={handleMailTo}
                  >
                    <Icon name="Mail" size="auto" />
                  </Button>
                </SimpleTooltip>
              )}
            </Stack>
            {canAddAmendment && (
              <Stack justify="end" gap={2} direction="horizontal">
                <Button variant="outline" size="sm" onClick={addChartAmendment}>
                  Add Amendments
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      <div
        ref={contentRef}
        className={cn(
          'w-full',
          isReadonly ? '[&_.ProseMirror]:p-0!' : 'rounded-lg border bg-white',
        )}
      >
        <EluveEditor
          key={`${textField.key}-${isReadonly}-${snippetCount}-${forceUpdate}`}
          disabled={isReadonly}
          content={content}
          extensions={!isReadonly ? [snippetExtension] : []}
          onUpdate={(editor) => {
            const content = getTiptapMarkdownContentFromEditor(editor);
            const normalizedContent = normalizeNewlinesFromMarkdown(content);
            if (
              areStringsEqualWithoutSpecialCharacters(normalizedContent, text)
            ) {
              return;
            }
            setTextValue(normalizedContent);
          }}
        />
        {appointmentId &&
          canViewAndEditAmendments &&
          amendments.map((amendment) =>
            amendment.chart_amendments
              .filter((chartAmentment) => chartAmentment.keyPath === key)
              .map((chartAmendment) => (
                <Amendment
                  appointmentId={appointmentId}
                  key={chartAmendment.id}
                  block={textField}
                  chartAmendmentId={chartAmendment.id}
                />
              )),
          )}
      </div>
    </Stack>
  );
};

const CheckboxBlockRenderer: React.FC<{ checkbox: CheckboxBlock }> = ({
  checkbox,
}) => {
  const { groupDepth, isReadonly } = useArtifactContext();
  const { label, options } = checkbox;
  const [checkboxOptions, setCheckboxOptions] = useDebounceSaveBlock(
    checkbox,
    options,
  );

  const depth = groupDepth + 1;
  const { tag: HeadingTag, className: headingClass } =
    getHeadingPropsForDepth(depth);

  return (
    <Stack gap={2}>
      {label && <HeadingTag className={headingClass}>{label}</HeadingTag>}
      <Stack fullWidth={false} gap={2}>
        {checkboxOptions.map(({ isChecked, label: optionLabel }, i) => (
          <Stack key={i} direction="horizontal" fullWidth={false} gap={2}>
            <Checkbox
              defaultChecked={isChecked}
              disabled={isReadonly}
              onCheckedChange={(checked) => {
                if (typeof checked === 'boolean') {
                  setCheckboxOptions((prev) => {
                    const newOptions: typeof prev = [];
                    for (const option of prev) {
                      if (option.label === optionLabel) {
                        newOptions.push({
                          label: option.label,
                          isChecked: checked,
                        });
                      } else {
                        newOptions.push(option);
                      }
                    }
                    return newOptions;
                  });
                }
              }}
            />
            <label className="text-xs font-medium text-text-primary">
              {optionLabel}
            </label>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const RangeRenderer: React.FC<{ range: RangeBlock }> = ({ range }) => {
  const { groupDepth, isReadonly } = useArtifactContext();
  const { max, min, step, value, label } = range;

  const [rangeValue, setRangeValue] = useDebounceSaveBlock(range, value ?? 0);

  const depth = groupDepth + 1;
  const { tag: HeadingTag, className: headingClass } =
    getHeadingPropsForDepth(depth);

  return (
    <Stack gap={2}>
      <HeadingTag className={headingClass}>
        {label ?? 'Value'}: {rangeValue}
      </HeadingTag>
      <Stack direction="horizontal" gap={3} align="center">
        <Slider
          min={min}
          max={max}
          value={[rangeValue]}
          step={step}
          disabled={isReadonly}
          onValueChange={([v]) => {
            if (!isNil(v) && !isReadonly) {
              setRangeValue(v);
            }
          }}
        />
      </Stack>
    </Stack>
  );
};

const GroupRenderer: React.FC<{ group: GroupBlock }> = ({ group }) => {
  const { blocks, label } = group;
  const {
    groupDepth,
    isReadonly,
    keyPath,
    email,
    canViewAndEditAmendments,
    showActions,
    compact,
  } = useArtifactContext();

  const depth = groupDepth + 1;
  const { tag: HeadingTag, className: headingClass } =
    getHeadingPropsForDepth(depth);

  const contextValue = useMemo(() => {
    return {
      groupDepth: groupDepth + 1,
      isReadonly,
      keyPath: [...keyPath, group.key],
      email,
      canViewAndEditAmendments,
      showActions,
      compact,
    };
  }, [
    groupDepth,
    isReadonly,
    keyPath,
    group.key,
    email,
    canViewAndEditAmendments,
    showActions,
    compact,
  ]);

  return (
    <ArtifactContext.Provider value={contextValue}>
      <Stack gap={2}>
        {label && <HeadingTag className={headingClass}>{label}</HeadingTag>}
        {blocks.map((b) => {
          return <BlockRenderer key={b.key} block={b} />;
        })}
      </Stack>
    </ArtifactContext.Provider>
  );
};

const BlockRenderer: React.FC<{
  block: Block;
}> = ({ block }) => {
  const children = match(block)
    .with({ type: 'text' }, (text) => (
      <TextFieldBlockRenderer textField={text} />
    ))
    .with({ type: 'checkbox' }, (checkbox) => (
      <CheckboxBlockRenderer checkbox={checkbox} />
    ))
    .with({ type: 'group' }, (group) => <GroupRenderer group={group} />)
    .with({ type: 'range' }, (range) => <RangeRenderer range={range} />)
    .exhaustive();

  return children;
};

export interface DynamicArtifactRendererProps {
  isReadonly: boolean;
  blocks: Block[];
  email?: string | null;
  canViewAndEditAmendments?: boolean;
  showActions?: boolean;
  compact?: boolean;
}

export const DynamicArtifactRenderer: React.FC<
  DynamicArtifactRendererProps & Partial<DynamicArtifactSaveContextProps>
> = ({
  blocks,
  isReadonly,
  canViewAndEditAmendments = false,
  saveBlock = defaultArtifactSaveContextProps.saveBlock,
  onPendingChanges = defaultArtifactSaveContextProps.onPendingChanges,
  insertChartAmendment = defaultArtifactSaveContextProps.insertChartAmendment,
  email,
  showActions = true,
  compact = false,
}) => {
  const contextValue = useMemo(
    () => ({
      isReadonly,
      canViewAndEditAmendments,
      groupDepth: 0,
      keyPath: [],
      email,
      showActions,
      compact,
    }),
    [isReadonly, email, canViewAndEditAmendments, showActions, compact],
  );

  const saveContextValue: DynamicArtifactSaveContextProps = useMemo(
    () => ({
      saveBlock,
      onPendingChanges,
      insertChartAmendment,
      email,
    }),
    [saveBlock, onPendingChanges, email, insertChartAmendment],
  );

  return (
    <DynamicArtifactSaveContext.Provider value={saveContextValue}>
      <ArtifactContext.Provider value={contextValue}>
        <Stack gap={4}>
          {blocks.map((b, i) => (
            <BlockRenderer key={b.key + i} block={b} />
          ))}
        </Stack>
      </ArtifactContext.Provider>
    </DynamicArtifactSaveContext.Provider>
  );
};
